import { Controller } from "@hotwired/stimulus"

const PLACEMENTS = new Set(["top", "bottom", "left", "right", "auto"])
const FALLBACK_PLACEMENTS = ["top", "right", "bottom", "left"]
const URL_ATTRIBUTES = new Set(["href", "src"])
const SAFE_URL_PATTERN = /^\s*(https?:|mailto:|tel:|#|\/)/i

const ALLOWED_TAGS = new Set([
  "a",
  "b",
  "br",
  "code",
  "em",
  "i",
  "small",
  "span",
  "strong",
  "u",
])

const ALLOWED_ATTRIBUTES = new Set([
  "aria-hidden",
  "aria-label",
  "href",
  "rel",
  "role",
  "target",
  "title",
])

/**
 * Advanced tooltip controller with sanitization and rich content support.
 * Handles positioning, triggers (hover/focus/click), and accessibility.
 * Content can be provided via data attributes or title.
 */
export default class extends Controller {
  static values = {
    animation: { type: Boolean, default: true },
    container: { type: String, default: "body" },
    customClass: String,
    delay: Number,
    fallbackPlacements: Array,
    hideDelay: Number,
    html: { type: Boolean, default: false },
    interactive: { type: Boolean, default: false },
    offset: { type: Number, default: 8 },
    placement: { type: String, default: "top" },
    sanitize: { type: Boolean, default: true },
    showDelay: Number,
    templateSelector: String,
    title: String,
    titleSelector: String,
    trigger: { type: String, default: "hover focus" },
  }

  connect() {
    this.visible = false
    this.showTimeout = null
    this.hideTimeout = null
    this.animationTimeout = null
    this.tooltipElement = null
    this.triggerModesCache = this.parseTriggerModes()
    this.containerElementCache = this.resolveContainerElement()

    this.boundToggle = this.toggle.bind(this)
    this.boundPointerEnter = this.handlePointerEnter.bind(this)
    this.boundPointerLeave = this.handlePointerLeave.bind(this)
    this.boundFocusIn = this.handleFocusIn.bind(this)
    this.boundFocusOut = this.handleFocusOut.bind(this)
    this.boundTooltipPointerEnter = this.handleTooltipPointerEnter.bind(this)
    this.boundTooltipPointerLeave = this.handleTooltipPointerLeave.bind(this)
    this.boundOutsideClick = this.handleOutsideClick.bind(this)
    this.boundReposition = this.updatePosition.bind(this)
    this.boundEscape = this.handleWindowKeydown.bind(this)

    this.installPublicApi()
    this.storeOriginalTitle()
    this.setupTriggers()
  }

  disconnect() {
    this.removeTriggers()
    this.removeGlobalListeners()
    this.clearTimers()
    this.destroyTooltip()
    this.restoreOriginalTitle()
    delete this.element.tooltipController
  }

  triggerValueChanged() {
    if (!this.boundToggle) return

    this.removeTriggers()
    this.triggerModesCache = this.parseTriggerModes()
    this.setupTriggers()
  }

  containerValueChanged() {
    this.containerElementCache = this.resolveContainerElement()
    this.updatePosition()
  }

  toggle(event) {
    event?.preventDefault()
    this.visible ? this.hide() : this.show()
  }

  show() {
    if (this.visible || this.showTimeout || !this.hasContent() || this.disabled()) return
    if (this.emit("show").defaultPrevented) return

    this.clearHideTimer()
    const delay = this.showDelayValue || this.delayValue || 0

    this.showTimeout = window.setTimeout(() => {
      this.showTimeout = null
      this.renderTooltip()
      if (!this.tooltipElement) return

      this.visible = true
      this.element.setAttribute("aria-describedby", this.tooltipId())
      this.addGlobalListeners()
      this.updatePosition()

      requestAnimationFrame(() => {
        if (!this.tooltipElement) return
        this.tooltipElement.classList.add("show")
        this.updatePosition()
        this.emit("shown")
      })
    }, delay)
  }

  hide() {
    if (!this.visible && !this.showTimeout) return
    if (this.visible && this.emit("hide").defaultPrevented) return

    this.clearShowTimer()
    const delay = this.hideDelayValue || this.delayValue || 0

    this.hideTimeout = window.setTimeout(() => {
      this.hideTimeout = null
      if (!this.tooltipElement) {
        this.visible = false
        this.removeGlobalListeners()
        this.element.removeAttribute("aria-describedby")
        return
      }

      this.tooltipElement.classList.remove("show")

      const finish = () => {
        this.animationTimeout = null
        this.destroyTooltip()
        this.visible = false
        this.element.removeAttribute("aria-describedby")
        this.removeGlobalListeners()
        this.emit("hidden")
      }

      if (this.animationValue) {
        this.animationTimeout = window.setTimeout(finish, 150)
      } else {
        finish()
      }
    }, delay)
  }

  enable() {
    this.element.removeAttribute("data-tooltip-disabled")
  }

  disable() {
    this.element.setAttribute("data-tooltip-disabled", "true")
    this.hide()
  }

  toggleEnabled() {
    this.disabled() ? this.enable() : this.disable()
  }

  update() {
    if (!this.visible) return

    this.updateMarkup()
    this.updatePosition()
  }

  handlePointerEnter(event) {
    if (!this.triggerModes().includes("hover")) return
    if (event.pointerType === "touch") return

    this.clearHideTimer()
    this.show()
  }

  handlePointerLeave(event) {
    if (!this.triggerModes().includes("hover")) return
    if (event.pointerType === "touch") return
    if (this.interactiveValue && this.tooltipElement?.matches(":hover")) return

    this.hide()
  }

  handleFocusIn() {
    if (!this.triggerModes().includes("focus")) return

    this.clearHideTimer()
    this.show()
  }

  handleFocusOut() {
    if (!this.triggerModes().includes("focus")) return

    requestAnimationFrame(() => {
      if (this.element.contains(document.activeElement)) return
      if (this.interactiveValue && this.tooltipElement?.contains(document.activeElement)) return
      this.hide()
    })
  }

  handleTooltipPointerEnter() {
    if (!this.interactiveValue) return
    this.clearHideTimer()
  }

  handleTooltipPointerLeave() {
    if (!this.interactiveValue) return
    if (!this.triggerModes().includes("hover")) return
    if (this.element.matches(":hover")) return

    this.hide()
  }

  handleOutsideClick(event) {
    if (!this.visible) return
    if (this.triggerModes().includes("manual")) return
    if (!this.triggerModes().includes("click")) return
    if (this.element.contains(event.target)) return
    if (this.tooltipElement?.contains(event.target)) return

    this.hide()
  }

  handleWindowKeydown(event) {
    if (event.key !== "Escape") return
    this.hide()
  }

  setupTriggers() {
    const modes = this.triggerModes()
    if (modes.includes("manual")) return

    if (modes.includes("click")) {
      this.element.addEventListener("click", this.boundToggle)
    }

    if (modes.includes("hover")) {
      this.element.addEventListener("pointerenter", this.boundPointerEnter)
      this.element.addEventListener("pointerleave", this.boundPointerLeave)
    }

    if (modes.includes("focus")) {
      this.element.addEventListener("focusin", this.boundFocusIn)
      this.element.addEventListener("focusout", this.boundFocusOut)
    }
  }

  removeTriggers() {
    this.element.removeEventListener("click", this.boundToggle)
    this.element.removeEventListener("pointerenter", this.boundPointerEnter)
    this.element.removeEventListener("pointerleave", this.boundPointerLeave)
    this.element.removeEventListener("focusin", this.boundFocusIn)
    this.element.removeEventListener("focusout", this.boundFocusOut)
  }

  renderTooltip() {
    if (this.tooltipElement) return

    this.tooltipElement = this.buildTooltipElement()
    this.updateMarkup()

    const container = this.containerElement()
    if (container !== document.body && getComputedStyle(container).position === "static") {
      container.style.position = "relative"
    }

    container.appendChild(this.tooltipElement)

    if (this.interactiveValue && this.triggerModes().includes("hover")) {
      this.tooltipElement.addEventListener("pointerenter", this.boundTooltipPointerEnter)
      this.tooltipElement.addEventListener("pointerleave", this.boundTooltipPointerLeave)
    }
  }

  buildTooltipElement() {
    const tooltip = document.createElement("div")
    tooltip.id = this.tooltipId()
    tooltip.className = this.tooltipClasses()
    tooltip.setAttribute("role", "tooltip")
    tooltip.setAttribute("data-tooltip-placement", this.resolvedPlacement())
    tooltip.setAttribute("data-tooltip-interactive", String(this.interactiveValue))
    tooltip.innerHTML = this.templateMarkup()
    return tooltip
  }

  updateMarkup() {
    if (!this.tooltipElement) return

    const title = this.resolvedTitle()
    this.tooltipElement.className = this.tooltipClasses()
    this.tooltipElement.setAttribute("data-tooltip-placement", this.resolvedPlacement())
    this.tooltipElement.setAttribute("data-tooltip-interactive", String(this.interactiveValue))

    const inner = this.tooltipElement.querySelector(".tooltip-inner")
    this.setNodeContent(inner, title.content, title.html)
  }

  updatePosition() {
    if (!this.visible || !this.tooltipElement) return

    const placement = this.resolveAutoPlacement()
    this.tooltipElement.setAttribute("data-tooltip-placement", placement)

    const triggerRect = this.element.getBoundingClientRect()
    const tooltipRect = this.tooltipElement.getBoundingClientRect()
    const { left, top } = this.calculatePosition(triggerRect, tooltipRect, placement, this.offsetValue)

    if (this.containerValue === "body") {
      this.tooltipElement.style.left = `${Math.round(left + window.scrollX)}px`
      this.tooltipElement.style.top = `${Math.round(top + window.scrollY)}px`
      return
    }

    const container = this.containerElement()
    const containerRect = container.getBoundingClientRect()
    this.tooltipElement.style.left = `${Math.round(left - containerRect.left + container.scrollLeft)}px`
    this.tooltipElement.style.top = `${Math.round(top - containerRect.top + container.scrollTop)}px`
  }

  calculatePosition(triggerRect, tooltipRect, placement, offset) {
    const centeredLeft = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
    const centeredTop = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2

    switch (placement) {
      case "bottom":
        return { left: centeredLeft, top: triggerRect.bottom + offset }
      case "left":
        return { left: triggerRect.left - tooltipRect.width - offset, top: centeredTop }
      case "right":
        return { left: triggerRect.right + offset, top: centeredTop }
      default:
        return { left: centeredLeft, top: triggerRect.top - tooltipRect.height - offset }
    }
  }

  resolveAutoPlacement() {
    const preferred = this.resolvedPlacement()
    if (preferred !== "auto") return preferred
    if (!this.tooltipElement) return "top"

    const triggerRect = this.element.getBoundingClientRect()
    const tooltipRect = this.tooltipElement.getBoundingClientRect()
    const placements = [...this.fallbackPlacementList(), ...FALLBACK_PLACEMENTS]
    const uniquePlacements = placements.filter((placement, index) => placements.indexOf(placement) === index)

    return uniquePlacements.find((placement) => this.fitsViewport(triggerRect, tooltipRect, placement)) || "top"
  }

  fitsViewport(triggerRect, tooltipRect, placement) {
    const padding = 8
    const { left, top } = this.calculatePosition(triggerRect, tooltipRect, placement, this.offsetValue)

    return left >= padding &&
      top >= padding &&
      left + tooltipRect.width <= window.innerWidth - padding &&
      top + tooltipRect.height <= window.innerHeight - padding
  }

  destroyTooltip() {
    if (this.tooltipElement && this.interactiveValue && this.triggerModes().includes("hover")) {
      this.tooltipElement.removeEventListener("pointerenter", this.boundTooltipPointerEnter)
      this.tooltipElement.removeEventListener("pointerleave", this.boundTooltipPointerLeave)
    }

    this.tooltipElement?.remove()
    this.tooltipElement = null
  }

  tooltipClasses() {
    return ["tooltip", this.animationValue ? "fade" : null, this.customClassValue || null].filter(Boolean).join(" ")
  }

  templateMarkup() {
    if (this.hasTemplateSelectorValue) {
      const template = document.querySelector(this.templateSelectorValue)
      return template?.innerHTML || this.defaultTemplate()
    }

    return this.defaultTemplate()
  }

  defaultTemplate() {
    return `
      <div class="tooltip-arrow"></div>
      <div class="tooltip-inner"></div>
    `
  }

  resolvedTitle() {
    const selectorMarkup = this.hasTitleSelectorValue ? this.htmlFromSelector(this.titleSelectorValue) : null
    const rawTitle = selectorMarkup || this.titleValue || this.element.dataset.bsTitle || this.originalTitle || ""

    if (!this.htmlValue) {
      return { present: rawTitle.trim().length > 0, content: rawTitle, html: false }
    }

    const sanitized = this.sanitizeValue ? this.sanitizeHtml(rawTitle) : rawTitle
    return { present: this.textFromHtml(sanitized).trim().length > 0, content: sanitized, html: true }
  }

  hasContent() {
    return this.resolvedTitle().present
  }

  resolvedPlacement() {
    return PLACEMENTS.has(this.placementValue) ? this.placementValue : "top"
  }

  fallbackPlacementList() {
    return (this.hasFallbackPlacementsValue ? this.fallbackPlacementsValue : FALLBACK_PLACEMENTS)
      .filter((placement) => FALLBACK_PLACEMENTS.includes(placement))
  }

  parseTriggerModes() {
    return this.triggerValue.split(/\s+/).filter(Boolean)
  }

  triggerModes() {
    return this.triggerModesCache || this.parseTriggerModes()
  }

  containerElement() {
    return this.containerElementCache || this.resolveContainerElement()
  }

  resolveContainerElement() {
    if (this.containerValue === "body") return document.body
    return document.querySelector(this.containerValue) || document.body
  }

  tooltipId() {
    if (!this._tooltipId) {
      if (!this.element.id) {
        this.element.id = `tooltip-trigger-${this.uniqueId()}`
      }

      this._tooltipId = `${this.element.id}-tooltip`
    }

    return this._tooltipId
  }

  uniqueId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return Math.random().toString(16).slice(2, 10)
  }

  htmlFromSelector(selector) {
    const node = document.querySelector(selector)
    return node ? node.innerHTML.trim() : ""
  }

  sanitizeHtml(html) {
    const template = document.createElement("template")
    template.innerHTML = html

    Array.from(template.content.querySelectorAll("*")).forEach((element) => {
      if (!ALLOWED_TAGS.has(element.tagName.toLowerCase())) {
        element.replaceWith(document.createTextNode(element.textContent || ""))
        return
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase()
        const value = attribute.value
        const allowed = ALLOWED_ATTRIBUTES.has(name) || name.startsWith("data-") || name.startsWith("aria-")
        const unsafeUrl = URL_ATTRIBUTES.has(name) && !SAFE_URL_PATTERN.test(value)
        const eventHandler = name.startsWith("on")

        if (!allowed || unsafeUrl || eventHandler) {
          element.removeAttribute(attribute.name)
        }
      })

      if (element.tagName.toLowerCase() === "a" && element.getAttribute("target") === "_blank") {
        element.setAttribute("rel", "noopener noreferrer")
      }
    })

    return template.innerHTML
  }

  setNodeContent(node, content, html = false) {
    if (!node) return

    if (html) {
      node.innerHTML = content
    } else {
      node.textContent = content
    }
  }

  textFromHtml(html) {
    const template = document.createElement("template")
    template.innerHTML = html
    return template.content.textContent || ""
  }

  addGlobalListeners() {
    document.addEventListener("click", this.boundOutsideClick)
    document.addEventListener("scroll", this.boundReposition, true)
    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("keydown", this.boundEscape)
  }

  removeGlobalListeners() {
    document.removeEventListener("click", this.boundOutsideClick)
    document.removeEventListener("scroll", this.boundReposition, true)
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("keydown", this.boundEscape)
  }

  clearTimers() {
    this.clearShowTimer()
    this.clearHideTimer()
    this.clearAnimationTimer()
  }

  clearShowTimer() {
    window.clearTimeout(this.showTimeout)
    this.showTimeout = null
  }

  clearHideTimer() {
    window.clearTimeout(this.hideTimeout)
    this.hideTimeout = null
  }

  clearAnimationTimer() {
    window.clearTimeout(this.animationTimeout)
    this.animationTimeout = null
  }

  storeOriginalTitle() {
    this.originalTitle = this.element.getAttribute("title") || ""
    if (this.originalTitle) {
      this.element.dataset.tooltipOriginalTitle = this.originalTitle
      this.element.removeAttribute("title")
    }
  }

  restoreOriginalTitle() {
    if (this.originalTitle) {
      this.element.setAttribute("title", this.originalTitle)
    }
  }

  installPublicApi() {
    this.element.tooltipController = this
  }

  disabled() {
    return this.element.hasAttribute("disabled") || this.element.dataset.tooltipDisabled === "true"
  }

  emit(name) {
    return this.dispatch(name, { prefix: "bs", cancelable: true })
  }
}